import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, FormLabel, Row, Spinner } from 'react-bootstrap';
import moment from 'moment';
import axiosInstance from '../../../services/AxiosInstance';
import Api from '../../../constants/Api';
import { errorAlert } from '../../components/alert/defaultAlert';
import DashboardSectionHeader from '../../components/Common/DashboardSectionHeader';
import InputDate from '../../components/Forms/Element/InputDate';
import PageTitle from '../../layouts/PageTitle';
import { useBranch } from '../../../context/BranchContext';
import './appointmentReview.css';

const STARS = [1, 2, 3, 4, 5];

// Abaixo disso a média é ruído: uma única opinião move o resultado inteiro, e sem esse
// aviso um 5,00 de uma resposta se parece com um 5,00 de vinte.
const MIN_RELIABLE_SAMPLE = 3;

// Faixas de cor da média. Abaixo de 3 o atendimento tem problema; de 3 a 4 é aceitável
// mas merece atenção; acima disso está bem avaliado.
const getRatingTone = (average) => {
    if (average === null || average === undefined) return 'neutral';
    if (average < 3) return 'danger';
    if (average < 4) return 'warning';
    return 'success';
};

const formatAverage = (average) => (average === null || average === undefined ? '--' : Number(average).toFixed(2));
const getInitials = (name = '') => name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase() || '?';

// Estrelas cheias até a média arredondada, para a nota ser lida sem passar pelo número.
const RatingStars = ({ value }) => (
    <span className="review-report-stars">
        {STARS.map((star) => (
            <i
                key={star}
                className={`fa-solid fa-star ${value !== null && value !== undefined && Math.round(value) >= star ? 'is-active' : ''}`}
            />
        ))}
    </span>
);

const AppointmentReviewReport = () => {
    const { selectedBranch } = useBranch();
    // Padrão de 30 dias: período curto o bastante para refletir a operação atual e longo o
    // bastante para acumular respostas.
    const [startDate, setStartDate] = useState(moment().subtract(30, 'days').toDate());
    const [endDate, setEndDate] = useState(new Date());
    const [summary, setSummary] = useState([]);
    // Indicadores do topo, contados no servidor pela mesma regra das linhas por colaborador.
    const [stats, setStats] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(false);
    // Ao clicar num colaborador, a lista de comentários passa a mostrar só os dele.
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    // Sem convite enviado não existe nota. Saber se o envio está ligado é o que permite
    // explicar um relatório vazio em vez de só constatar que ele está vazio.
    const [inviteEnabled, setInviteEnabled] = useState(null);

    const buildPeriodParams = useCallback(() => ({
        startDate: `${moment(startDate).format('YYYY-MM-DD')}T00:00:00`,
        endDate: `${moment(endDate).format('YYYY-MM-DD')}T23:59:59`,
        ...(selectedBranch?.id ? { branchId: selectedBranch.id } : {})
    }), [startDate, endDate, selectedBranch?.id]);

    const load = useCallback(() => {
        setLoading(true);
        const params = buildPeriodParams();

        Promise.all([
            axiosInstance.get(`${Api.appointment.review}/summary`, { params }),
            axiosInstance.get(Api.appointment.review, { params }),
            axiosInstance.get(`${Api.appointment.review}/stats`, { params })
        ])
            .then(([summaryResponse, reviewsResponse, statsResponse]) => {
                setSummary(Array.isArray(summaryResponse.data) ? summaryResponse.data : []);
                setReviews(Array.isArray(reviewsResponse.data) ? reviewsResponse.data : []);
                setStats(statsResponse.data || null);
            })
            .catch((error) => errorAlert(error))
            .finally(() => setLoading(false));
    }, [buildPeriodParams]);

    useEffect(() => {
        load();
    }, [load]);

    // Lido uma vez só: a configuração não muda enquanto a tela está aberta.
    useEffect(() => {
        axiosInstance.get(Api.parameter.appointment)
            .then((resp) => {
                const parameter = Array.isArray(resp.data) ? resp.data[0] : resp.data;
                setInviteEnabled(Boolean(parameter?.sendReviewInvite));
            })
            .catch(() => {
                // Falhar aqui não pode derrubar o relatório: sem a informação, a tela apenas
                // deixa de sugerir a causa do vazio.
                setInviteEnabled(null);
            });
    }, []);

    // Se o colaborador filtrado sai do período consultado, o filtro deixa de fazer sentido.
    useEffect(() => {
        setSelectedEmployeeId((current) =>
            current && summary.some((item) => item.employeeId === current) ? current : null);
    }, [summary]);

    // Totais do período vindos do servidor. Antes eram somados aqui a partir das linhas, o que
    // repetia a conta do backend e deixava de fora o convite de atendimento sem colaborador.
    const totals = {
        invited: Number(stats?.invited || 0),
        sent: Number(stats?.sent || 0),
        noEmail: Number(stats?.noEmail || 0),
        failed: Number(stats?.failed || 0),
        answered: Number(stats?.answered || 0),
        average: stats?.averageRating ?? null,
        responseRate: Number(stats?.responseRate || 0)
    };
    const notSent = totals.noEmail + totals.failed;

    // Só comentários interessam nesta lista: uma nota sem texto já está contada no resumo.
    const visibleReviews = useMemo(() => reviews
        .filter((review) => review.comment)
        .filter((review) => !selectedEmployeeId || review.employeeId === selectedEmployeeId),
    [reviews, selectedEmployeeId]);

    const selectedEmployeeName = useMemo(
        () => summary.find((item) => item.employeeId === selectedEmployeeId)?.employeeName,
        [summary, selectedEmployeeId]
    );

    return (
        <>
            <PageTitle activeMenu="Avaliações" motherMenu="Agendamentos" />

            <div className="review-report-page">
            <Card className="review-report-card review-report-filter-card">
                <Card.Body>
                    <DashboardSectionHeader
                        icon="fa-sliders"
                        accentClass="is-info"
                        eyebrow="Período"
                        title="Filtros do relatório"
                        subtitle="Escolha o intervalo das avaliações que deseja analisar."
                    />
                    <Row className="align-items-end g-3">
                        <Col xs={12} sm={6} lg={3}>
                            <FormLabel>Data inicial</FormLabel>
                            <InputDate selected={startDate} onChange={(value) => setStartDate(value)} />
                        </Col>
                        <Col xs={12} sm={6} lg={3}>
                            <FormLabel>Data final</FormLabel>
                            <InputDate selected={endDate} onChange={(value) => setEndDate(value)} />
                        </Col>
                        <Col xs={12} lg="auto">
                            <Button className="review-report-consult-button" onClick={load} disabled={loading}>
                                <i className="fa-solid fa-magnifying-glass me-2"></i>
                                Consultar
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Indicadores depois do filtro, colados no resultado, como nas demais listagens.
                Mostram o caminho do convite: criado, enviado, não enviado (e por quê) e
                respondido. Sem o meio do caminho, uma taxa baixa parecia "o e-mail não chega"
                quando o motivo era cliente sem e-mail cadastrado. */}
            <div className="review-report-metrics">
                {[
                    { key: 'average', label: 'Média geral', value: formatAverage(totals.average), tone: getRatingTone(totals.average) },
                    { key: 'invited', label: 'Convites', value: totals.invited, hint: 'Atendimentos concluídos' },
                    { key: 'sent', label: 'Enviados', value: totals.sent, hint: 'Por e-mail ou WhatsApp' },
                    {
                        key: 'not-sent',
                        label: 'Não enviados',
                        value: notSent,
                        tone: notSent > 0 ? 'warning' : null,
                        hint: `${totals.noEmail} sem e‑mail · ${totals.failed} com falha`
                    },
                    { key: 'answered', label: 'Respostas', value: totals.answered },
                    { key: 'rate', label: 'Taxa de resposta', value: `${totals.responseRate.toFixed(1)}%`, hint: 'Sobre os enviados' }
                ].map((metric) => (
                    <div key={metric.key} className="review-report-metric">
                        <span className="review-report-metric__label">{metric.label}</span>
                        <strong className={`review-report-metric__value ${metric.tone ? `is-${metric.tone}` : ''}`}>
                            {metric.value}
                        </strong>
                        {metric.hint && <small className="review-report-metric__hint">{metric.hint}</small>}
                    </div>
                ))}
            </div>

            <Card className="review-report-card review-report-results-card">
                <Card.Body>
                    {loading ? (
                        <div className="review-report-loading">
                            <Spinner animation="border" />
                            <span>Carregando avaliações...</span>
                        </div>
                    ) : (
                        <>
                            <DashboardSectionHeader
                                icon="fa-ranking-star"
                                accentClass="is-warning"
                                eyebrow="Desempenho"
                                title="Notas por colaborador"
                                subtitle="Todos os colaboradores que prestam serviço, da menor média para a maior. Quem ainda não tem nota aparece no fim. Clique em um colaborador para ver só os comentários dele."
                            />

                            {/* Relatório sem convite não é um problema do relatório. Ou o envio está
                                desligado, ou nenhum atendimento foi concluído no período. Dizer qual
                                dos dois evita que alguém fique esperando notas que nunca virão. */}
                            {totals.invited === 0 && (
                                <Alert variant={inviteEnabled === false ? 'warning' : 'info'} className="review-report-notice">
                                    {inviteEnabled === false ? (
                                        <>
                                            <strong>O convite de avaliação está desligado.</strong> Nenhum cliente é
                                            convidado a avaliar ao concluir o atendimento, então não haverá notas aqui.
                                            Ligue em <strong>Configurações &gt; Agendamento</strong>, no campo de envio
                                            do convite de avaliação.
                                        </>
                                    ) : (
                                        <>
                                            <strong>Nenhum convite foi enviado no período.</strong> O convite nasce
                                            quando um atendimento é concluído: sem atendimento concluído no intervalo
                                            escolhido, não há o que avaliar.
                                        </>
                                    )}
                                </Alert>
                            )}

                            {/* O que a empresa consegue resolver sozinha: cadastrar o e-mail do
                                cliente. Falha de envio é com o suporte, que confere no serviço
                                de envio. */}
                            {notSent > 0 && (
                                <Alert variant="warning" className="review-report-notice">
                                    {totals.noEmail > 0 && (
                                        <div>
                                            <strong>{totals.noEmail} {totals.noEmail === 1 ? 'cliente não recebeu' : 'clientes não receberam'} o convite por não ter e‑mail cadastrado.</strong>{' '}
                                            Cadastre o e‑mail no cadastro do cliente para que os próximos atendimentos gerem o convite.
                                        </div>
                                    )}
                                    {totals.failed > 0 && (
                                        <div>
                                            <strong>{totals.failed} {totals.failed === 1 ? 'envio falhou' : 'envios falharam'}.</strong>{' '}
                                            O cliente tinha e-mail, mas a mensagem não saiu. Se continuar acontecendo, avise o suporte.
                                        </div>
                                    )}
                                </Alert>
                            )}

                            {summary.length === 0 ? (
                                <p className="review-report-empty">
                                    Nenhum colaborador que presta serviço foi encontrado na filial selecionada.
                                </p>
                            ) : (
                                <div className="review-report-table-wrap">
                                    <table className="review-report-table">
                                        <thead>
                                            <tr>
                                                <th>Colaborador</th>
                                                <th>Média</th>
                                                <th>Respostas</th>
                                                <th>Taxa</th>
                                                <th>Distribuição</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.map((item) => (
                                                <tr
                                                    key={item.employeeId}
                                                    className={selectedEmployeeId === item.employeeId ? 'is-selected' : ''}
                                                    onClick={() => setSelectedEmployeeId(
                                                        selectedEmployeeId === item.employeeId ? null : item.employeeId
                                                    )}
                                                >
                                                    <td>
                                                        <div className="review-report-employee">
                                                            <span className="review-report-avatar">
                                                                {getInitials(item.employeeName)}
                                                            </span>
                                                            <div>
                                                                <strong>{item.employeeName}</strong>
                                                                <small>
                                                                    {item.invitedCount} convite(s) · {item.sentCount ?? item.invitedCount} enviado(s)
                                                                    {item.noEmailCount > 0 && ` · ${item.noEmailCount} sem e-mail`}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {item.averageRating === null || item.averageRating === undefined ? (
                                                            // Nenhuma nota inventada para quem não foi avaliado: a média
                                                            // vazia é informação, não um espaço a ser preenchido.
                                                            <span className="review-report-no-rating">Sem avaliações</span>
                                                        ) : (
                                                            <>
                                                                <span className={`review-report-average is-${getRatingTone(item.averageRating)}`}>
                                                                    {formatAverage(item.averageRating)}
                                                                </span>
                                                                <RatingStars value={item.averageRating} />
                                                                {item.answeredCount < MIN_RELIABLE_SAMPLE && (
                                                                    <span
                                                                        className="review-report-sample"
                                                                        title={`Média apurada sobre ${item.answeredCount} resposta(s). Uma opinião isolada move muito o resultado.`}
                                                                    >
                                                                        amostra pequena
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <strong>{item.answeredCount}</strong>
                                                        {/* A taxa é sobre os enviados, então o "de" também é. */}
                                                        <span className="review-report-muted"> de {item.sentCount ?? item.invitedCount}</span>
                                                    </td>
                                                    <td>
                                                        <div className="review-report-rate">
                                                            <strong>{Number(item.responseRate).toFixed(1)}%</strong>
                                                            <span>
                                                                <i style={{ width: `${Math.min(Number(item.responseRate), 100)}%` }} />
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="review-report-distribution">
                                                            {[
                                                                { stars: 5, count: item.ratingFive },
                                                                { stars: 4, count: item.ratingFour },
                                                                { stars: 3, count: item.ratingThree },
                                                                { stars: 2, count: item.ratingTwo },
                                                                { stars: 1, count: item.ratingOne }
                                                            ].map(({ stars, count }) => (
                                                                <span key={stars} title={`${count} nota(s) ${stars}`}>
                                                                    {stars}★ {count}
                                                                </span>
                                                            ))}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="mt-4">
                                <DashboardSectionHeader
                                    icon="fa-comments"
                                    accentClass="is-success"
                                    eyebrow="Voz do cliente"
                                    title={selectedEmployeeName ? `Comentários de ${selectedEmployeeName}` : 'Comentários dos clientes'}
                                    subtitle="Apenas avaliações em que o cliente escreveu algo."
                                    action={selectedEmployeeName && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setSelectedEmployeeId(null)}
                                        >
                                            <i className="fa-solid fa-xmark me-2" />
                                            Limpar filtro
                                        </Button>
                                    )}
                                />
                            </div>

                            {visibleReviews.length === 0 ? (
                                <p className="review-report-empty">Nenhum comentário no período selecionado.</p>
                            ) : (
                                <div className="review-report-comments">
                                    {visibleReviews.map((review) => (
                                        <div key={review.id} className="review-report-comment">
                                            <div className="review-report-comment__head">
                                                <div>
                                                    <span className="review-report-comment__avatar">
                                                        {getInitials(review.customerName || 'Cliente')}
                                                    </span>
                                                    <div>
                                                        <strong>{review.customerName || 'Cliente'}</strong>
                                                        <span>Atendido por {review.employeeName}</span>
                                                    </div>
                                                </div>
                                                {/* Pelo WhatsApp o comentário pode chegar antes da nota: o cliente
                                                    escreve primeiro e escolhe a nota depois, na lista. Até lá a
                                                    avaliação aparece sem nota e com a data do pedido. */}
                                                <div className="review-report-comment__meta">
                                                    {review.whatsappSent && (
                                                        <Badge bg="light" text="dark" title="Respondida pelo WhatsApp">
                                                            <i className="fa-brands fa-whatsapp" />
                                                        </Badge>
                                                    )}
                                                    <Badge bg="light" text="dark">
                                                        {review.rating ? `${review.rating}★` : 'Sem nota'}
                                                    </Badge>
                                                    <small>{moment(review.respondedAt || review.invitedAt).format('DD/MM/YYYY')}</small>
                                                </div>
                                            </div>
                                            <p><i className="fa-solid fa-quote-left" />{review.comment}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
            </div>
        </>
    );
};

export default AppointmentReviewReport;
